/**
 * Registry of data-sync connectors (spec 0035 / request 0139).
 * Analogous to orgParamGroupRegistry — register at bootstrap, read at runtime.
 */
class SyncConnectorRegistry {
    constructor() {
        this.byKey = new Map();
    }
    register(connector) {
        this.byKey.set(connector.key, connector);
        this.assertAcyclic();
    }
    get(key) {
        return this.byKey.get(key);
    }
    /** Registration order (not dependency order). */
    list() {
        return Array.from(this.byKey.values());
    }
    /**
     * Connectors in dependency order (dependsOn first). Throws if the graph has a cycle
     * (cycles are also rejected at {@link register}).
     */
    listInDependencyOrder() {
        return topologicalSort(this.list());
    }
    /** Fail fast on register — cycles are a configuration error, not a runtime surprise. */
    assertAcyclic() {
        topologicalSort(this.list());
    }
}
/**
 * Kahn topological sort. Unknown `dependsOn` keys are ignored (dependency not
 * registered yet / out of this set). Cycle → Error.
 */
export function topologicalSort(connectors) {
    const byKey = new Map(connectors.map((c) => [c.key, c]));
    const indegree = new Map();
    const dependents = new Map();
    for (const c of connectors) {
        indegree.set(c.key, 0);
        dependents.set(c.key, []);
    }
    for (const c of connectors) {
        for (const dep of c.dependsOn ?? []) {
            if (!byKey.has(dep))
                continue;
            indegree.set(c.key, (indegree.get(c.key) ?? 0) + 1);
            dependents.get(dep).push(c.key);
        }
    }
    const queue = connectors.filter((c) => (indegree.get(c.key) ?? 0) === 0).map((c) => c.key);
    const ordered = [];
    while (queue.length > 0) {
        const key = queue.shift();
        ordered.push(byKey.get(key));
        for (const next of dependents.get(key) ?? []) {
            const nextDeg = (indegree.get(next) ?? 0) - 1;
            indegree.set(next, nextDeg);
            if (nextDeg === 0)
                queue.push(next);
        }
    }
    if (ordered.length !== connectors.length) {
        throw new Error('[SyncConnectorRegistry] Cycle detected in connector dependsOn graph. ' +
            'Fix the registration order / dependsOn declarations.');
    }
    return ordered;
}
export const syncConnectorRegistry = new SyncConnectorRegistry();
//# sourceMappingURL=syncConnectorRegistry.js.map